// src/utils/animations.js - Complete Animation System

import React, { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './accessibility';

// Animation timing functions
export const easingFunctions = {
  easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easeOut: 'cubic-bezier(0, 0, 0.2, 1)',
  easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
  bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  elastic: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)'
};

// Animation durations
export const durations = {
  instant: 0,
  fast: 150,
  normal: 300,
  slow: 500,
  slower: 750
};

// Stagger animation hook
export const useStaggeredAnimation = (items, delay = 100) => {
  const [visibleItems, setVisibleItems] = useState(new Set());
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      setVisibleItems(new Set(items.map((_, i) => i)));
      return;
    }

    items.forEach((_, index) => {
      setTimeout(() => {
        setVisibleItems(prev => new Set([...prev, index]));
      }, index * delay);
    });
  }, [items.length, delay, prefersReducedMotion]);

  return visibleItems;
};

// Scroll-triggered animations
export const useScrollAnimation = (threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin: '50px' }
    );

    observer.observe(element);
    return () => observer.unobserve(element);
  }, [threshold]);

  return [ref, isVisible];
};

// Page transition animations
export const pageTransitions = {
  fadeIn: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.3 }
  },
  slideUp: {
    initial: { y: 20, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: -20, opacity: 0 },
    transition: { duration: 0.4, ease: easingFunctions.easeOut }
  },
  slideLeft: {
    initial: { x: 100, opacity: 0 },
    animate: { x: 0, opacity: 1 },
    exit: { x: -100, opacity: 0 },
    transition: { duration: 0.4, ease: easingFunctions.easeInOut }
  },
  scale: {
    initial: { scale: 0.9, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 1.1, opacity: 0 },
    transition: { duration: 0.3, ease: easingFunctions.spring }
  }
};

// Micro-interaction animations
export const microAnimations = {
  button: {
    tap: { scale: 0.95 },
    hover: { scale: 1.02, y: -1 },
    loading: {
      rotate: [0, 360],
      transition: { duration: 1, repeat: Infinity, ease: 'linear' }
    }
  },
  card: {
    hover: {
      y: -4,
      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
      transition: { duration: 0.2 }
    },
    tap: { scale: 0.98 }
  },
  icon: {
    hover: { rotate: 5, scale: 1.1 },
    tap: { rotate: -5, scale: 0.9 }
  }
};

// Celebration animations
export const celebrationAnimations = {
  confetti: {
    particles: Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      color: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4'][Math.floor(Math.random() * 5)],
      size: Math.random() * 6 + 2,
      angle: Math.random() * 360,
      velocity: Math.random() * 5 + 2
    }))
  },
  fireworks: {
    bursts: Array.from({ length: 3 }, (_, i) => ({
      id: i,
      x: 20 + i * 30,
      y: 30 + Math.random() * 40,
      delay: i * 200
    }))
  }
};

// Animation configuration objects (pure JS)
export const getAnimationClasses = (animation, isVisible) => {
  const animations = {
    fadeIn: isVisible ? 'animate-fade-in' : 'opacity-0',
    slideUp: isVisible ? 'animate-slide-up' : 'opacity-0 translate-y-4',
    slideLeft: isVisible ? 'animate-slide-left' : 'opacity-0 translate-x-4',
    scale: isVisible ? 'animate-scale-in' : 'opacity-0 scale-95'
  };
  return animations[animation] || animations.fadeIn;
};

// Floating animation hook
export const useFloatingAnimation = (duration = 3000) => {
  const [isFloating, setIsFloating] = useState(true);

  const floatingStyle = isFloating ? {
    animation: `float ${duration}ms ease-in-out infinite`,
  } : {};

  return { floatingStyle, setIsFloating };
};

// Parallax scroll effect
export const useParallax = (speed = 0.5) => {
  const [offset, setOffset] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      setOffset(scrolled * speed);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return [ref, { transform: `translateY(${offset}px)` }];
};

// Magnetic hover effect
export const useMagneticHover = (strength = 0.3) => {
  const ref = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const handleMouseMove = (e) => {
      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      const x = (e.clientX - centerX) * strength;
      const y = (e.clientY - centerY) * strength;

      setPosition({ x, y });
    };

    const handleMouseLeave = () => {
      setPosition({ x: 0, y: 0 });
    };

    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [strength]);

  return [ref, {
    transform: `translate(${position.x}px, ${position.y}px)`,
    transition: 'transform 0.1s ease-out'
  }];
};

// Typewriter effect
export const useTypewriter = (text, speed = 50, delay = 0) => {
  const [displayText, setDisplayText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  useEffect(() => {
    let timeout;

    const startTyping = () => {
      let i = 0;
      const typeNextChar = () => {
        if (i < text.length) {
          setDisplayText(text.slice(0, i + 1));
          i++;
          timeout = setTimeout(typeNextChar, speed);
        } else {
          setIsComplete(true);
        }
      };
      typeNextChar();
    };

    timeout = setTimeout(startTyping, delay);

    return () => clearTimeout(timeout);
  }, [text, speed, delay]);

  return { displayText, isComplete };
};

// Confetti animation configuration - RENAMED for consistency
export const createConfetti = (isActive, duration = 3000, particleCount = 50) => {
  if (!isActive) return { particles: [], isVisible: false };

  const particles = Array.from({ length: particleCount }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: -10,
    color: ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#F59E0B', '#EF4444'][Math.floor(Math.random() * 7)],
    size: Math.random() * 6 + 2,
    velocity: Math.random() * 3 + 2,
    rotation: Math.random() * 360,
    rotationSpeed: Math.random() * 10 - 5
  }));

  return { particles, isVisible: true, duration };
};

// Keep both exports for backward compatibility
export const createConfettiParticles = createConfetti;

// Spring animation hook
export const useSpringAnimation = (isActive, config = {}) => {
  const {
    tension = 300,
    friction = 30,
    mass = 1
  } = config;

  const [style, setStyle] = useState({
    transform: 'scale(1)',
    opacity: 1
  });

  useEffect(() => {
    if (isActive) {
      setStyle({
        transform: 'scale(1.1)',
        opacity: 1,
        transition: `all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)`
      });

      const timer = setTimeout(() => {
        setStyle({
          transform: 'scale(1)',
          opacity: 1,
          transition: `all 0.2s ease-out`
        });
      }, 150);

      return () => clearTimeout(timer);
    }
  }, [isActive]);

  return style;
};

// CSS animations to add to your global styles
export const cssAnimations = `
  @keyframes fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes slide-up {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slide-left {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes scale-in {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    33% { transform: translateY(-10px) rotate(1deg); }
    66% { transform: translateY(-5px) rotate(-1deg); }
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  @keyframes ripple {
    0% {
      transform: scale(0);
      opacity: 1;
    }
    100% {
      transform: scale(4);
      opacity: 0;
    }
  }

  @keyframes confetti {
    0% {
      transform: translateY(0) rotate(0deg);
      opacity: 1;
    }
    100% {
      transform: translateY(100vh) rotate(720deg);
      opacity: 0;
    }
  }

  @keyframes bounce-in {
    0% {
      transform: scale(0.3);
      opacity: 0;
    }
    50% {
      transform: scale(1.05);
    }
    70% {
      transform: scale(0.9);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }

  @keyframes pulse-glow {
    0%, 100% {
      box-shadow: 0 0 5px rgba(59, 130, 246, 0.5);
    }
    50% {
      box-shadow: 0 0 20px rgba(59, 130, 246, 0.8);
    }
  }

  .animate-fade-in { animation: fade-in 0.5s ease-out; }
  .animate-slide-up { animation: slide-up 0.5s ease-out; }
  .animate-slide-left { animation: slide-left 0.5s ease-out; }
  .animate-scale-in { animation: scale-in 0.5s ease-out; }
  .animate-float { animation: float 3s ease-in-out infinite; }
  .animate-shimmer { animation: shimmer 2s infinite; }
  .animate-ripple { animation: ripple 0.6s linear; }
  .animate-bounce-in { animation: bounce-in 0.6s ease-out; }
  .animate-pulse-glow { animation: pulse-glow 2s ease-in-out infinite; }

  /* Hover utilities */
  .hover-lift {
    transition: transform 0.2s ease-out;
  }
  .hover-lift:hover {
    transform: translateY(-2px);
  }

  .hover-scale {
    transition: transform 0.2s ease-out;
  }
  .hover-scale:hover {
    transform: scale(1.02);
  }

  .hover-glow {
    transition: box-shadow 0.3s ease-out;
  }
  .hover-glow:hover {
    box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
  }

  /* Reduced motion support */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
    }

    .animate-float,
    .animate-shimmer,
    .animate-pulse-glow {
      animation: none !important;
    }
  }
`;

// Sound utility function (simple implementation)
export const playSound = (soundPath, volume = 0.5) => {
  try {
    const audio = new Audio(soundPath);
    audio.volume = Math.max(0, Math.min(1, volume));
    audio.play().catch(e => {
      console.warn("Could not play sound:", e.message);
    });
  } catch (error) {
    console.error("Error creating audio:", error);
  }
};

// Export everything
export default {
  easingFunctions,
  durations,
  useStaggeredAnimation,
  useScrollAnimation,
  pageTransitions,
  microAnimations,
  celebrationAnimations,
  getAnimationClasses,
  createConfetti,
  createConfettiParticles,
  playSound,
  useFloatingAnimation,
  useParallax,
  useMagneticHover,
  useTypewriter,
  useSpringAnimation,
  cssAnimations
};